import * as React from 'react';

import moment from 'moment';

import { useFreeTrialCTACopyExperimentData } from 'bundles/coursera-plus-common/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import ArticleRichEntityCard from 'bundles/unified-seo-content-common/components/entity-cards/ArticleRichEntityCard';
import EntityCardPlaceholder from 'bundles/unified-seo-content-common/components/entity-cards/EntityCardPlaceholder';
import type { ENTITY_TYPES } from 'bundles/unified-seo-content-common/constants';
import useEntityCardData from 'bundles/unified-seo-content-common/hooks/useEntityCardData';
import useEntityCardLink from 'bundles/unified-seo-content-common/hooks/useEntityCardLink';
import type { EntityCardInputProps, EntityType } from 'bundles/unified-seo-content-common/types';
import { getEntityTypeLabel } from 'bundles/unified-seo-content-common/utils';
import { getCourseQueryConfig } from 'bundles/unified-seo-content-common/utils/entityCardDataQueryUtils';

import _t from 'i18n!nls/articles';

type CourseCardEntityType = Extract<EntityType, typeof ENTITY_TYPES.course | typeof ENTITY_TYPES.project>;

type Props = EntityCardInputProps & {
  entityType: CourseCardEntityType;
};

const DESC_MAX_LEN = 160;
const removeLineBreakFromString = (s: string) => s.replace(/(\r\n|\n|\r)/gm, ' ');
const limitDescriptionLength = (s: string, limit = DESC_MAX_LEN) => {
  return s.length > limit ? `${s.substring(0, s.lastIndexOf(String.fromCharCode(32), limit - 4) + 1)}...` : s;
};

const CourseEntityCard = ({ entityType, slug, customDescription }: Props) => {
  const { data, loading } = useEntityCardData(getCourseQueryConfig(slug));
  const cardLink = useEntityCardLink({ entityType, slug });
  const { isSessionBasedFreeTrialCopyEnabled, isUserBasedFreeTrialCopyEnabled } = useFreeTrialCTACopyExperimentData({
    isStandAloneCourse: (data?.specializations || [])?.length === 0,
    courseId: data?.id,
  });
  const shouldShowStartFreeTrialLabel = isSessionBasedFreeTrialCopyEnabled || isUserBasedFreeTrialCopyEnabled;

  if (loading) {
    return <EntityCardPlaceholder />;
  }

  if (!data || !data?.partners?.length) {
    return null;
  }

  let totalHours = moment.duration(data.material?.totalDuration).asHours();

  totalHours = totalHours > 1 ? Math.floor(totalHours) : 1;

  const articleRichEntityCardProps = {
    ctaHref: cardLink,
    ctaLabel: shouldShowStartFreeTrialLabel ? _t('Start free trial') : undefined,
    ctaAriaLabel: _t('Learn more about #{name}', { name: data.name }),
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    imageSrc: data.partners[0].squareLogo || '',
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    imageAlt: data.partners[0].name,
    title: data.name,
    subtitle: customDescription || limitDescriptionLength(removeLineBreakFromString(data.description || '')),
    cardMetaData: {
      entityType: getEntityTypeLabel(entityType).toLowerCase(),
      level: data.difficultyLevel || null,
      durationInHours: totalHours,
      skills: data.skills?.join(', '),
      averageFiveStarRating: data.ratings?.averageFiveStarRating?.toFixed(1),
      ratingCount: data.ratings?.ratingCount,
      totalEnrollmentCount: Number(data.totalEnrollmentCount).toLocaleString(),
    },
  };

  return <ArticleRichEntityCard {...articleRichEntityCardProps} />;
};

export default CourseEntityCard;
